import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Play, Tv, Film, Bookmark, Sparkles, Users, Shield, CreditCard, ChevronRight, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const Feature = ({ icon: Icon, title, desc }) => (
  <div className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur">
    <div className="w-11 h-11 rounded-xl bg-[#E50914]/15 text-[#E50914] flex items-center justify-center mb-3">
      <Icon size={20} />
    </div>
    <div className="text-white font-semibold">{title}</div>
    <div className="text-white/60 text-sm mt-1">{desc}</div>
  </div>
);

const Step = ({ n, title, desc }) => (
  <div className="flex gap-4">
    <div className="shrink-0 w-10 h-10 rounded-full bg-[#E50914] text-white font-bold flex items-center justify-center">{n}</div>
    <div>
      <div className="text-white font-semibold">{title}</div>
      <div className="text-white/60 text-sm mt-1">{desc}</div>
    </div>
  </div>
);

const Login = () => {
  const { login } = useAuth();
  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background collage */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-50"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1536440136628-849c177e76a1?auto=format&fit=crop&w=2400&q=80')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#07070b] via-[#07070b]/80 to-[#07070b]/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#07070b] via-transparent to-[#07070b]/40" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        <header className="px-10 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#E50914] flex items-center justify-center shadow-lg shadow-[#E50914]/30">
              <span className="font-display text-2xl text-white leading-none">B</span>
            </div>
            <div className="leading-tight">
              <div className="font-display text-2xl tracking-wider text-white">BLUNT TV</div>
              <div className="text-[10px] uppercase text-white/50 tracking-[0.2em]">TV Edition</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/about" className="hidden md:block px-4 py-2 text-white/70 hover:text-white text-sm">About</Link>
            <Link to="/contact" className="hidden md:block px-4 py-2 text-white/70 hover:text-white text-sm">Contact</Link>
            <button
              onClick={login}
              className="px-5 py-2.5 rounded-lg bg-[#E50914] text-white font-semibold hover:bg-[#f6121d] tv-focusable"
            >
              Sign in
            </button>
          </div>
        </header>

        {/* Hero */}
        <main className="flex-1 flex flex-col items-center justify-center text-center px-6 py-16">
          <span className="px-3 py-1 text-[11px] tracking-widest uppercase rounded-full bg-white/10 border border-white/20 text-white/80 mb-5">
            Now streaming • Built for your TV
          </span>
          <h1 className="font-display text-5xl md:text-7xl text-white leading-none max-w-4xl">
            Independent cinema + every trailer worth watching.
          </h1>
          <p className="text-white/75 text-lg mt-5 max-w-2xl">
            BLUNT TV streams an exclusive library of
            <span className="text-white font-semibold"> BLUNT Originals</span> — indie films and series from creators you can&apos;t find anywhere else —
            plus ad-free trailers and personalized recommendations for 10,000+ other movies and shows.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={login}
              className="tv-focusable inline-flex items-center gap-3 px-7 py-4 rounded-xl bg-white text-black font-semibold text-base hover:bg-white/90"
            >
              <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.7 5.8 29.6 3.5 24 3.5 12.7 3.5 3.5 12.7 3.5 24S12.7 44.5 24 44.5 44.5 35.3 44.5 24c0-1.2-.1-2.4-.3-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.7 5.8 29.6 3.5 24 3.5 16.1 3.5 9.4 8 6.3 14.7z"/><path fill="#4CAF50" d="M24 44.5c5.5 0 10.5-2.1 14.2-5.5l-6.6-5.4c-2 1.4-4.6 2.4-7.6 2.4-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.2 39.9 16 44.5 24 44.5z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.6 5.4C41.9 35.7 44.5 30.3 44.5 24c0-1.2-.1-2.4-.3-3.5z"/></svg>
              Continue with Google
            </button>
            <a href="#how" className="tv-focusable inline-flex items-center gap-2 px-6 py-4 rounded-xl bg-white/10 text-white font-semibold hover:bg-white/20 backdrop-blur">
              <Play size={16} /> How it works
            </a>
          </div>
          <div className="mt-3 text-white/40 text-xs">Secure sign-in via Google • No passwords • 7-day free browse</div>

          {/* Trust bar */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-white/50 text-xs uppercase tracking-widest">
            <span className="flex items-center gap-2"><Star size={12} className="text-amber-400 fill-amber-400" /> 10,000+ titles</span>
            <span className="flex items-center gap-2"><Tv size={12} /> Web & Roku</span>
            <span className="flex items-center gap-2"><Shield size={12} /> Ad-free</span>
            <span className="flex items-center gap-2"><CreditCard size={12} /> Cancel anytime</span>
          </div>
        </main>

        {/* Features grid */}
        <section className="relative z-10 px-10 pb-16 pt-4 max-w-6xl mx-auto">
          <h2 className="text-center text-white/50 text-xs uppercase tracking-widest mb-6">What you get</h2>
          <div className="grid md:grid-cols-4 gap-4">
            <Feature icon={Sparkles} title="BLUNT Originals" desc="Full HD streaming of exclusive indie films and series." />
            <Feature icon={Film} title="Trailers for everything" desc="Ad-free trailers for 10,000+ movies & shows." />
            <Feature icon={Tv} title="TV-first UI" desc="Designed for big screens with remote-friendly focus." />
            <Feature icon={Bookmark} title="Cross-device list" desc="Your My List follows you everywhere." />
          </div>
        </section>

        {/* How it works */}
        <section id="how" className="relative z-10 px-10 py-16 bg-gradient-to-b from-transparent to-black/40">
          <div className="max-w-4xl mx-auto">
            <div className="text-center">
              <span className="text-[11px] tracking-widest uppercase text-white/50">How it works</span>
              <h2 className="font-display text-4xl md:text-5xl text-white mt-2">Start watching in 60 seconds</h2>
            </div>
            <div className="mt-10 grid md:grid-cols-2 gap-x-10 gap-y-8">
              <Step n="1" title="Sign in with Google" desc="No passwords, no waiting. One click and you're in." />
              <Step n="2" title="Pick a profile" desc="Create up to 4 profiles. Kids mode hides mature content." />
              <Step n="3" title="Choose a plan" desc="Browse for free forever. Subscribe to unlock full HD BLUNT Originals." />
              <Step n="4" title="Watch anywhere" desc="Web today, Roku channel coming soon, Chromecast ready now." />
            </div>
          </div>
        </section>

        {/* Plans teaser */}
        <section className="relative z-10 px-10 py-16">
          <div className="max-w-5xl mx-auto">
            <div className="text-center">
              <span className="text-[11px] tracking-widest uppercase text-white/50">Simple pricing</span>
              <h2 className="font-display text-4xl md:text-5xl text-white mt-2">Cancel anytime. No contracts.</h2>
            </div>
            <div className="mt-10 grid md:grid-cols-3 gap-5">
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10">
                <div className="text-white/60 text-xs uppercase tracking-widest">Free</div>
                <div className="text-4xl font-bold text-white mt-2">$0</div>
                <div className="text-white/50 text-sm">Browse + trailers for 10,000+ titles</div>
              </div>
              <div className="p-6 rounded-2xl bg-[#E50914]/10 border border-[#E50914]/40 relative" data-testid="landing-plan-basic">
                <span className="absolute -top-3 left-6 px-3 py-0.5 rounded-full text-[10px] font-bold tracking-widest uppercase bg-[#E50914] text-white">Most popular</span>
                <div className="text-white/60 text-xs uppercase tracking-widest">Basic</div>
                <div className="text-4xl font-bold text-white mt-2">$2.99<span className="text-lg text-white/50">/mo</span></div>
                <div className="text-white/70 text-sm">All BLUNT TV episodes • Full HD</div>
                <div className="text-emerald-400 text-xs mt-1">7-day free trial</div>
              </div>
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10" data-testid="landing-plan-live">
                <div className="text-white/60 text-xs uppercase tracking-widest">Live Events</div>
                <div className="text-4xl font-bold text-white mt-2">$7.99<span className="text-lg text-white/50">/mo</span></div>
                <div className="text-white/70 text-sm">Everything in Basic + live events</div>
              </div>
            </div>
            <div className="mt-8 text-center">
              <button onClick={login} className="inline-flex items-center gap-2 px-7 py-3 rounded-lg bg-white text-black font-semibold hover:bg-white/90">
                Get started free <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="relative z-10 px-10 py-10 border-t border-white/10 text-white/50 text-sm">
          <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#E50914] flex items-center justify-center">
                <span className="font-display text-lg text-white leading-none">B</span>
              </div>
              <div>
                <div className="font-display text-white tracking-wider">BLUNT TV</div>
                <div className="text-[10px] uppercase tracking-[0.2em]">© {new Date().getFullYear()}</div>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/about" className="hover:text-white">About</Link>
              <Link to="/contact" className="hover:text-white">Contact</Link>
              <Link to="/terms" className="hover:text-white">Terms</Link>
              <Link to="/privacy" className="hover:text-white">Privacy</Link>
            </div>
          </div>
          <div className="max-w-6xl mx-auto mt-4 text-[11px] text-white/40">
            Discovery metadata provided by <a href="https://www.themoviedb.org/" target="_blank" rel="noreferrer" className="hover:text-white">TMDB</a>. BLUNT TV is not endorsed, certified, or affiliated with TMDB. Subscription grants access to BLUNT Originals only; third-party titles are available on their respective platforms.
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Login;
